import { memo, useState } from "react";
import { DeleteOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Typography } from "antd";
import dayjs from "dayjs";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { isSameYear } from "../../../../variables/functions";
import { computeImmatriculation } from "./data/donnees";
import { LOGGED_USER } from "../../../../variables/constants";
import { useParams } from "react-router-dom";
const { Option } = Select;

const EnginForm = ({ index, marques, carosseries, form, removeVehicle, openConducteurModal, garantiesNonObligatoires = [], onEnginsUpdate, setSameYear, categorie, editMode }) => {
    const param = useParams();
    const { Text } = Typography;
    const [organismeModal, setOrganismeModal] = useState({
        visible: false,
        data: null,
        field: null, // 'immatriculation' ou 'chassis'
        value: null
    });

    const getEngins = () => form.getFieldValue('engins') || [];
    const engins = getEngins();
    const engin = engins[index] || {};

    // Vérifier si châssis existe dans la BD
    const checkChassisInDB = async (chassis) => {
        if (!chassis) return false;
        if (editMode) return false;
        try {
            const response = await axiosInstance.get(`/api/engin/chassis/${chassis}`);
            return response.data || false;
        } catch (error) {
            console.error("Erreur lors de la vérification du châssis:", error);
            return false;
        }
    };

    // Vérifier l'organisme du contrat lié à l'engin
    const checkOrganismeContrat = async (enginId) => {
        try {
            const response = await axiosInstance.get(`/api/contrat/engin/${enginId}/organisme`);
            // Retourne { existe: true/false, organisme: "NOM_ORGANISME", dateDebut: "2023-01-01", dateFin: "2024-01-01" }
            return response.data;
        } catch (error) {
            console.error("Erreur lors de la vérification de l'organisme:", error);
            return null;
        }
    };

    // Vérifier si immatriculation existe déjà dans les engins précédents
    const checkImmatriculationInEngins = (immatriculation, currentIndex) => {
        const allEngins = getEngins();
        return allEngins.some((eng, idx) =>
            idx !== currentIndex &&
            eng.immatriculation &&
            eng.immatriculation.toLowerCase() === immatriculation.toLowerCase()
        );
    };

    // Vérifier si châssis existe déjà dans les engins précédents
    const checkChassisInEngins = (chassis, currentIndex) => {
        const allEngins = getEngins();
        return allEngins.some((eng, idx) =>
            idx !== currentIndex &&
            eng.numeroChassis &&
            eng.numeroChassis.toLowerCase() === chassis.toLowerCase()
        );
    };

    // Gérer la confirmation de l'utilisateur pour continuer malgré l'existence dans un autre organisme
    const handleOrganismeConfirm = async () => {
        const { data, field, value } = organismeModal;

        try {
            // Récupérer les détails complets de l'engin depuis le backend
            const enginData = organismeModal.data?.engins.find((engin) => engin.immatriculation === value);

            // Remplir automatiquement les champs du formulaire avec les données de l'engin
            const cur = [...getEngins()];
            cur[index] = {
                ...cur[index],
                // Données d'identification
                immatriculation: enginData.immatriculation || cur[index].immatriculation,
                typeSerie: enginData.typeSerie || cur[index].typeSerie,
                matricule: enginData.matricule || cur[index].matricule,
                numeroSerie: enginData.numeroSerie || cur[index].numeroSerie,
                numeroChassis: enginData.numeroChassis || cur[index].numeroChassis,

                // Données techniques
                marque: enginData.marque || cur[index].marque,
                energie: enginData.energie || cur[index].energie,
                usage: enginData.usage || cur[index].usage,
                volume: enginData.volume || enginData.puissance || cur[index].volume,
                puissance: enginData.puissance || enginData.volume || cur[index].puissance,
                nombrePlace: enginData.nombrePlace || cur[index].nombrePlace,
                genre: enginData.genre || cur[index].genre,
                moteur: enginData.moteur || cur[index].moteur,
                type: enginData.type || cur[index].type,
                carosserie: enginData.carosserie || cur[index].carosserie,

                // Valeurs
                valeurVenale: enginData.valeurVenale || cur[index].valeurVenale,
                valeurDeclaree: enginData.valeurDeclaree || cur[index].valeurDeclaree,
                valeurANeuf: enginData.valeurANeuf || cur[index].valeurANeuf,

                // Date de mise en circulation
                dateMiseEnCirculation: enginData.dateMiseEnCirculation ? dayjs(enginData.dateMiseEnCirculation) : cur[index].dateMiseEnCirculation,
            };

            // Mettre à jour le formulaire Ant Design
            form.setFieldsValue({ engins: cur });

            // Notifier le parent
            if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);

            // Fermer le modal
            setOrganismeModal({ visible: false, data: null, field: null, value: null });

        } catch (error) {
            console.error("Erreur lors de la récupération des données de l'engin:", error);
            // En cas d'erreur, on ferme quand même le modal
            setOrganismeModal({ visible: false, data: null, field: null, value: null });
        }
    };

    // Gérer l'annulation - l'utilisateur ne veut pas continuer
    const handleOrganismeCancel = () => {
        const { field, value } = organismeModal;

        // Réinitialiser le champ concerné
        const cur = [...getEngins()];
        if (field === 'immatriculation') {
            cur[index].immatriculation = '';
            cur[index].typeSerie = '';
            cur[index].matricule = '';
            cur[index].numeroSerie = '';
            form.setFieldsValue({
                engins: cur
            });
        } else if (field === 'chassis') {
            cur[index].numeroChassis = '';
            form.setFieldsValue({
                engins: cur
            });
        }

        setOrganismeModal({ visible: false, data: null, field: null, value: null });

        // Forcer la revalidation du champ
        if (field === 'immatriculation') {
            form.validateFields([['engins', index, 'typeSerie']]);
        } else if (field === 'chassis') {
            form.validateFields([['engins', index, 'numeroChassis']]);
        }
    };

    const handleEnginChange = (value, field, idx = index) => {
        const cur = [...getEngins()];
        cur[idx] = { ...(cur[idx] || {}), [field]: value };

        // if typeSerie/matricule/numeroSerie affecte immatriculation
        if (['numeroSerie', 'matricule', 'typeSerie'].includes(field)) {
            const v = cur[idx];
            const catCode = categorie?.codeCategorie || categorie;
            v.immatriculation = computeImmatriculation(catCode, v?.typeSerie, v?.numeroSerie, v?.matricule);
        }

        if (field === 'valeurVenale') {
            // reset carrosserie si catégorie changée
            cur[idx].valeurVenale = value;
            cur[idx].valeurDeclaree = value;
            cur[idx].valeurANeuf = value;
        }
        if (field === 'volume') {
            console.log("Volummmmmme " + value, " categorie" + categorie?.codeCategorie);
        }

        if (field === 'puissance') {
            console.log("Puissance " + value);
        }

        if (field === "volume" && !categorie?.codeCategorie?.startsWith("M")) {
            console.log("Volummmmmme " + value);
            cur[idx].puissance = parseInt(value);
        }

        if (field === 'garantiesSupplementaires') {
            cur[idx].garantiesSupplementaires = value;

            var gtys = [];
            value.forEach((e) => {
                let g = garantiesNonObligatoires.find((el) => el.codeId === e);
                gtys.push(g);
            });

            axiosInstance
                .post(`/api/sous-garantie/garanties`, JSON.stringify(gtys))
                .then((ress) => {
                    cur[idx].sousGaranties = ress.data;
                })
                .catch((errr) => {
                    console.log(errr);
                });
        }

        if (field === 'dateMiseEnCirculation') {
            const isy = isSameYear(value?.toISOString()) || true;
            setSameYear(isy);
        }

        // update Antd form and parent state
        form.setFieldsValue({ engins: cur });
        if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);
    };

    const isTpvCategory = () => {
        const code = (categorie?.codeCategorie || categorie || "").toString().toUpperCase();
        const name = (categorie?.nom || "").toString().toUpperCase();
        const usage = (engin?.usage || "").toString().toUpperCase();
        return (
            code.includes("TPV") ||
            name.includes("TPV") ||
            name.includes("TRANSPORT") ||
            usage === "TPV" ||
            usage.includes("TRANSPORT")
        );
    };

    const allowOptionalImmat = isTpvCategory();

    const typeSerieValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative && !value) return Promise.resolve();
        if (!value) return Promise.reject(new Error('Le type de série est requis'));
        return Promise.resolve();
    };

    const matriculeValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative && !value) return Promise.resolve();
        if (!value) return Promise.reject(new Error("L'immatriculation est requise"));
        return Promise.resolve();
    };

    const numeroSerieValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative) return Promise.resolve();
        if (engin?.typeSerie === 'TG' && !value) return Promise.reject(new Error('N° série requis'));
        return Promise.resolve();
    };

    const handleValeurVenaleBlur = (idx = index) => {
        // placeholder pour calculs éventuels après saisie de la valeur vénale
    };

    const handleShowConducteurModal = (idx = index) => {
        openConducteurModal(idx);
    };

    const removeEngin = (idx = index) => {
        removeVehicle(idx);
    };

    // Validator pour l'immatriculation
    const immatriculationValidator = {
        validator: async (_, value) => {
            // 1. Immatriculation vide → OK
            if (!engin.immatriculation) return Promise.resolve();

            // 2. Mode update : si l'immatriculation n'a pas changé → OK
            if (param?.contratId && engin.oldImmatriculation === engin.immatriculation) {
                return Promise.resolve();
            }

            // 3. Vérifier dans la liste des engins du formulaire
            const existsInEngins = checkImmatriculationInEngins(engin.immatriculation, index);
            if (existsInEngins) {
                return Promise.reject(new Error('Cette immatriculation existe déjà dans la liste des engins'));
            }

            const enginExistant = await checkOrganismeContrat(engin.immatriculation);
            console.log('enginExistant', enginExistant);
            if (enginExistant) {
                if (enginExistant?.organisme?.code !== LOGGED_USER?.profil?.organisme?.code) {
                    // Afficher le modal de confirmation
                    setOrganismeModal({
                        visible: true,
                        data: enginExistant,
                        field: 'immatriculation',
                        value: engin.immatriculation
                    });

                    // return Promise.reject(new Error('Vérification de l\'organisme en cours'));
                    return Promise.resolve();
                }

                // Si même organisme ou pas d'info organisme, erreur standard
                return Promise.reject(new Error('Cette immatriculation existe déjà dans la base de données'));
            }

            return Promise.resolve();
        }
    };

    // Validator pour le nombre de places
    const nombrePlaceValidator = {
        validator: (_, value) => {
            if (!value || !categorie) return Promise.resolve();

            const min = categorie.nombrePlaceMin || 0;
            const max = categorie.nombrePlaceMax || Infinity;

            if (value < min) {
                return Promise.reject(new Error(`Le nombre de places minimum est ${min} pour cette catégorie`));
            }
            if (value > max) {
                return Promise.reject(new Error(`Le nombre de places maximum est ${max} pour cette catégorie`));
            }

            return Promise.resolve();
        }
    };

    // Validator pour le châssis
    const chassisValidator = {
        validator: async (_, value) => {
            if (!value) return Promise.resolve();


            // 2. Mode update : si le numero de chassis n'a pas changé → OK
            if (param?.contratId && engin.oldNumeroChassis === engin.numeroChassis) {
                return Promise.resolve();
            }

            const existsInEngins = checkChassisInEngins(value, index);
            if (existsInEngins) {
                return Promise.reject(new Error('Ce numéro de châssis existe déjà dans la liste des engins'));
            }

            const enginExistant = await checkChassisInDB(value);
            if (enginExistant) {
                // Vérifier l'organisme du contrat
                const organismeInfo = await checkOrganismeContrat(enginExistant.id || enginExistant._id);

                if (organismeInfo && organismeInfo.existe && organismeInfo.autreOrganisme) {
                    // Afficher le modal de confirmation
                    setOrganismeModal({
                        visible: true,
                        data: organismeInfo,
                        field: 'chassis',
                        value: value
                    });

                    return Promise.reject(new Error('Vérification de l\'organisme en cours'));
                }

                // Si même organisme ou pas d'info organisme, erreur standard
                return Promise.reject(new Error('Ce numéro de châssis existe déjà dans la base de données'));
            }

            return Promise.resolve();
        }
    };

    // validator pour carosserie uniquement si catégorie MOTO
    const carrosserieRequiredRule = {
        validator: (_, value) => {
            const cat = form.getFieldValue(['engins'])?.[index]?.categorie;
            if (cat === 'MOTO' && !value) {
                return Promise.reject(new Error('La carrosserie est requise pour la catégorie MOTO'));
            }
            return Promise.resolve();
        }
    };

    const isMoto = categorie?.nom === 'MOTO';

    return (
        <>
            <Card
                key={index}
                className="mb-3"
                size="small"
                title={`Engin N°${index + 1}`}
                extra={
                    (getEngins().length > 1) && (
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeEngin(index)}
                        >
                            Supprimer
                        </Button>
                    )
                }
            >
                <Row gutter={16}>
                    {/* Immatriculation */}
                    <Col span={8}>
                        <Form.Item
                            label={
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Immatriculation {allowOptionalImmat && engin?.immatriculationFacultative ? "(Facultatif)" : "*"}</span>
                                </div>
                            }
                            name={['engins', index, 'typeSerie']}
                            rules={[{ validator: typeSerieValidator }, immatriculationValidator]}
                            validateTrigger={['onChange', 'onBlur']}
                        >
                            <Input.Group compact>
                                <Select
                                    value={engin.typeSerie}
                                    onChange={(val) => handleEnginChange(val, 'typeSerie', index)}
                                    style={{ width: '20%' }}
                                >
                                    <Option value="TG">TG</Option>
                                    <Option value="Autre">Autre</Option>
                                </Select>
                                {/* matricule */}
                                <Form.Item name={['engins', index, 'matricule']} noStyle rules={[{ validator: matriculeValidator }]}>
                                    <Input
                                        value={engin.matricule}
                                        onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'matricule', index)}
                                        style={{ width: '50%' }}
                                        placeholder="Immatriculation"
                                    />
                                </Form.Item>
                                {engin?.typeSerie === 'TG' && (
                                    <Form.Item name={['engins', index, 'numeroSerie']} noStyle rules={[{ validator: numeroSerieValidator }]}>
                                        <Input
                                            value={engin.numeroSerie}
                                            onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'numeroSerie', index)}
                                            style={{ width: '30%' }}
                                            placeholder="N° série"
                                        />
                                    </Form.Item>
                                )}
                            </Input.Group>
                        </Form.Item>
                    </Col>

                    {/* Marque */}
                    <Col span={8}>
                        <Form.Item label="Marque" name={['engins', index, 'marque']} rules={[{ required: true, message: 'La marque est requise' }]}>
                            <Select
                                value={engin.marque}
                                onChange={(val) => handleEnginChange(val, 'marque', index)}
                                placeholder="Sélectionner la marque"
                                showSearch
                                options={marques?.map((marque, id) => ({
                                    label: marque?.libelleParametre,
                                    value: marque?.libelleParametre,
                                    key: id
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    {/* Énergie */}
                    <Col span={8}>
                        <Form.Item label="Énergie" name={['engins', index, 'energie']} rules={[{ required: true, message: 'L\'énergie est requise' }]}>
                            <Select
                                value={engin.energie}
                                onChange={(val) => handleEnginChange(val, 'energie', index)}
                            >
                                <Option value="ESSENCE">ESSENCE</Option>
                                <Option value="DIESEL">DIESEL</Option>
                                <Option value="ELECTRIQUE">ÉLECTRIQUE</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Usage */}
                    <Col span={8}>
                        <Form.Item label="Usage" name={['engins', index, 'usage']} rules={[{ required: true, message: "L'usage est requis" }]}>
                            <Select
                                value={engin.usage}
                                onChange={(val) => handleEnginChange(val, 'usage', index)}
                                disabled={!isMoto}
                            >
                                <Option value="PRIVE">PRIVE</Option>
                                <Option value="TAXI">TAXI</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Volume */}
                    <Col span={8}>
                        <Form.Item
                            label={isMoto ? "Volume (cm³) / Puissance" : "Puissance (CV)"}
                            name={['engins', index, 'volume']}
                            rules={[{ required: true, message: 'Le volume est requis' }]}>
                            <InputNumber
                                value={engin.volume}
                                onChange={(val) => handleEnginChange(val, 'volume', index)}
                                style={{ width: '100%' }}
                                placeholder={isMoto ? "Volume / Puissance" : "Puissance"}
                            />
                        </Form.Item>
                    </Col>

                    {/* Nombre de places */}
                    <Col span={8}>
                        <Form.Item label="Nombre de places" name={['engins', index, 'nombrePlace']} rules={[{ required: true, message: 'Le nombre de places est requis' }, nombrePlaceValidator]}>
                            <InputNumber
                                value={engin.nombrePlace}
                                onChange={(val) => handleEnginChange(val, 'nombrePlace', index)}
                                style={{ width: '100%' }}
                                placeholder="Nombre de places"
                                min={categorie?.nombrePlaceMin || 1}
                                max={categorie?.nombrePlaceMax || 100}
                            />
                        </Form.Item>
                    </Col>

                    {/* Genre */}
                    {categorie?.codeCategorie?.startsWith('M') ? <Col span={8}>
                        <Form.Item label="Genre" name={['engins', index, 'genre']} rules={[{ required: true, message: 'Le genre est requis' }]}>
                            <Select
                                value={engin.genre}
                                onChange={(val) => handleEnginChange(val, 'genre', index)}
                            >
                                <Option value="VELOMOTEUR">Vélomoteur</Option>
                                <Option value="TRICYCLE">Tricycle</Option>
                                <Option value="MOTOCYCLETTE">Motocyclette</Option>
                                <Option value="CYCLOMOTEUR">Cyclomoteur</Option>
                            </Select>
                        </Form.Item>
                    </Col> : null}

                    {/* N° Moteur */}
                    <Col span={8}>
                        <Form.Item label="N° Moteur" name={['engins', index, 'moteur']} rules={[{ required: true, message: 'N° moteur requis' }]}>
                            <Input
                                value={engin.moteur}
                                onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'moteur', index)}
                                placeholder="Numéro de moteur"
                            />
                        </Form.Item>
                    </Col>

                    {/* Type */}
                    <Col span={8}>
                        <Form.Item label="Type" name={['engins', index, 'type']} rules={[{ required: true, message: 'Type requis' }]}>
                            <Input
                                value={engin.type}
                                onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'type', index)}
                                placeholder="Type"
                            />
                        </Form.Item>
                    </Col>

                    {/* N° Châssis */}
                    <Col span={8}>
                        <Form.Item label="N° Châssis" name={['engins', index, 'numeroChassis']} rules={[{ required: true, message: 'N° châssis requis' }, chassisValidator]} validateTrigger={['onChange', 'onBlur']}>
                            <Input
                                value={engin.numeroChassis}
                                onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'numeroChassis', index)}
                                placeholder="Numéro de châssis"
                            />
                        </Form.Item>
                    </Col>

                    {/* Carrosserie - obligatoire uniquement si catégorie MOTO */}
                    {engins[0].categorie === 'MOTO' ? <Col span={8}>
                        <Form.Item label="Carosserie" name={['engins', index, 'carosserie']} rules={[carrosserieRequiredRule]}>
                            <Select
                                value={engin.carosserie}
                                onChange={(val) => handleEnginChange(val, 'carosserie', index)}
                                placeholder="Sélectionner la carrosserie"
                                options={carosseries?.map((c) => ({ label: c.libelleParametre, value: c.libelleParametre }))}
                            />
                        </Form.Item>
                    </Col> : null}

                    {/* Valeur vénale */}
                    <Col span={8}>
                        <Form.Item label="Valeur vénale" name={['engins', index, 'valeurVenale']} rules={[{ required: true, message: 'Valeur vénale requise' }]}>
                            <InputNumber
                                value={engin.valeurVenale}
                                onChange={(val) => handleEnginChange(val, 'valeurVenale', index)}
                                onBlur={() => handleValeurVenaleBlur(index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur vénale"
                            />
                        </Form.Item>
                    </Col>

                    {/* Valeur déclarée */}
                    <Col span={8}>
                        <Form.Item label="Valeur déclarée" name={['engins', index, 'valeurDeclaree']} rules={[{ required: true, message: 'Valeur déclarée requise' }]}>
                            <InputNumber
                                value={engin.valeurDeclaree}
                                onChange={(val) => handleEnginChange(val, 'valeurDeclaree', index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur Déclarée"
                            />
                        </Form.Item>
                    </Col>

                    {/* Valeur à neuf */}
                    <Col span={8}>
                        <Form.Item label="Valeur à neuf" name={['engins', index, 'valeurANeuf']} rules={[{ required: true, message: 'Valeur à neuf requise' }]}>
                            <InputNumber
                                value={engin.valeurANeuf}
                                onChange={(val) => handleEnginChange(val, 'valeurANeuf', index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur à neuf"
                            />
                        </Form.Item>
                    </Col>

                    {/* Date de mise en circulation */}
                    <Col span={8}>
                        <Form.Item
                            label="Année de mise en circulation"
                            name={['engins', index, 'dateMiseEnCirculation']}
                            rules={[{ required: true, message: 'Année requise' }]}
                        >
                            <DatePicker
                                value={engin?.dateMiseEnCirculation ? dayjs(engin.dateMiseEnCirculation) : null}
                                onChange={(date) => handleEnginChange(date ?? null, 'dateMiseEnCirculation', index)}
                                picker="year"
                                style={{ width: '100%' }}
                                placeholder="Sélectionner l'année"
                                disabledDate={(current) => current && current > dayjs()}
                            />
                        </Form.Item>
                    </Col>

                    {/* Garanties supplémentaires */}
                    <Col span={8}>
                        <Form.Item label="Garanties supplémentaires" name={['engins', index, 'garantiesSupplementaires']}>
                            <Select
                                mode="multiple"
                                value={engin.garantiesSupplementaires}
                                onChange={(val) => handleEnginChange(val, 'garantiesSupplementaires', index)}
                                placeholder="Sélectionner les garanties"
                            >
                                {garantiesNonObligatoires.map((gar, id) => (
                                    <Option key={id} value={gar.codeId}>
                                        {gar.nom}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Conducteur habituel */}
                    <Col span={24}>
                        {engin?.useSouscripteur === false ? (
                            <Card size="small" className="bg-light">
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Text strong>Conducteur habituel : </Text>
                                        <Text>
                                            {engin.personne?.nom} {engin.personne?.prenom}
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Button
                                            size="small"
                                            onClick={() => handleShowConducteurModal(index)}
                                            className="me-2"
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => {
                                                // retirer conducteur: remettre useSouscripteur true et vider personne
                                                handleEnginChange(true, 'useSouscripteur', index);
                                                handleEnginChange({}, 'personne', index);
                                            }}
                                        >
                                            Retirer
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        ) : (
                            <Alert
                                message="Par défaut, le souscripteur du contrat sera le conducteur habituel"
                                type="info"
                                action={
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<UserOutlined />}
                                        onClick={() => handleShowConducteurModal(index)}
                                    >
                                        Préciser un conducteur différent
                                    </Button>
                                }
                            />
                        )}
                    </Col>
                </Row>
            </Card>

            {/* Modal de confirmation pour organisme différent */}
            <Modal
                title={
                    <span>
                        <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                        Engin assuré dans un autre organisme
                    </span>
                }
                open={organismeModal.visible}
                onOk={handleOrganismeConfirm}
                onCancel={handleOrganismeCancel}
                okText="Continuer la production"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
                width={600}
            >
                <Alert
                    message="Attention"
                    description={
                        <div>
                            <p>
                                Cet engin ({organismeModal.field === 'immatriculation' ? 'immatriculation' : 'châssis'}: <strong>{organismeModal.value}</strong>)
                                était assuré dans un autre organisme :
                            </p>
                            <ul style={{ marginTop: 10, marginBottom: 10 }}>
                                <li><strong>Organisme :</strong> {organismeModal?.data?.organisme?.raisonSociale}</li>
                                <li><strong>Période :</strong> Du {organismeModal?.data?.dateEffet ? dayjs(organismeModal?.data?.dateEffet).format('DD/MM/YYYY') : 'N/A'} au {organismeModal?.data?.dateEcheance ? dayjs(organismeModal?.data?.dateEcheance).format('DD/MM/YYYY') : 'N/A'}</li>
                            </ul>
                            <p style={{ marginTop: 15 }}>
                                Voulez-vous vraiment continuer la production de ce contrat ?
                            </p>
                        </div>
                    }
                    type="warning"
                    showIcon
                />
            </Modal>
        </>
    );
};

export default memo(EnginForm);
